<template>
    <h1 class="page-title">La liste des sociétés</h1>
    <div class="card">
        <DataTable lazy dataKey="uuid"
            stateStorage="local"
            ref="dt"
            :state-key="localStorageKey"
            :value="tableData"
            :paginator="true"
            :rows="rows"
            :rowHover="true"
            removableSort
            v-model:selection="selectedCompanies"
            :metaKeySelection="false"
            :loading="isLoading"
            :totalRecords="totalRecords"
            :currentPage="currentPage"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            :rowsPerPageOptions="rowsPerPageOptions"
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
            @page="onPaginate($event)">
            <template #loading> Chargement... </template>
            <template #header>
                <div class="flex flex-row flex-nowrap card-header border-0">
                    <div class="flex card-toolbar w-full">
                        <div class="w-full">
                            <span class="p-input-icon-left w-10/12">
                                <i class="pi pi-search" />
                                <InputText type="text" class="w-9/12" v-model="criteria"
                                    placeholder="Recherche par société" @input="onSearchInputDt()" />
                            </span>
                        </div>
                    </div>
                    <div class="card-toolbar w-full">
                        <div class="flex flex-col justify-center">
                            <p>Nombre total de sociétés : {{ totalRecords }}</p>
                        </div>
                    </div>
                </div>
            </template>
            <div class="card-body pt-0">
                <Column field="name" header="Société">
                    <template #body="slotProps">
                        <span> {{ slotProps.data.name }}</span>
                    </template>
                </Column>
                <Column headerStyle="width: 12em;">
                    <template #header>
                        <div>Actions</div>
                    </template>
                    <template #body="slotProps">
                        <Button size="small" icon="pi pi-eye" class="p-button-eye"
                            :class="`${slotProps.data.uuid}`"
                            style="margin-right: 0.5em" title="Voir" @click="router.push({ name: 'hiddenMasterProductByCompanyList', params: { uuid: slotProps.data.uuid } })">
                        </Button>
                    </template>
                </Column>
            </div>
            <template #empty>Aucun résultat trouvé.</template>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeMount } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { fetchCompanies } from "@/_services/company/company";
import { ICompany } from "@/models/company/ICompany";
import router from "@/router";

const localStorageKey = "selectedCompanies";
const isLoading = ref<boolean>(true);
const companies = ref<Array<ICompany>>([]);
const tableData = ref<Array<ICompany>>(companies);
const totalSelectedCompanies = ref<number>(0);
const selectedCompanies = ref(Array<ICompany>());
const currentPage = ref<number>(0);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const criteria = ref<string>("");
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];

onBeforeMount(() => {
    localStorage.removeItem(localStorageKey);
});

onMounted(async () => {
    totalSelectedCompanies.value = selectedCompanies.value.length;
    await populateDataTableWithLocalStorageFilters();
});

function onSearchInputDt() {
    let search = criteria.value.trim();
    showSpinner();
    populateDatatable(
        rows.value,
        page.value,
        "" != search ? search : "",
    );
}

const showSpinner = () => {
    isLoading.value = true;
}

async function populateDatatable(
    itemsPerPage,
    page,
    criteria?
) {
    showSpinner();
    const [loadingDt, totalRecordsDt, tableDataDt] = await fetchCompanies(
        itemsPerPage,
        page,
        criteria,
        criteria !== "" ? criteria : undefined,
    );
    totalRecords.value = totalRecordsDt.value;
    isLoading.value = loadingDt.value;
    tableData.value = tableDataDt.value;
}

async function populateDataTableWithLocalStorageFilters(
    itemsPerPage?: number,
    page?: number
) {
    let localStorageRowPerPage = getLocalStorageRowsPerPage();
    let localStorageFisrtPageState = getLocalStorageFisrtPageState();
    
    let currentPage = page;
    if (!currentPage) {
        currentPage = localStorageFisrtPageState / localStorageRowPerPage + 1;
    }

    let currentItemsPerPage = itemsPerPage;
    if (!currentItemsPerPage) {
        currentItemsPerPage = localStorageRowPerPage;
    }

    populateDatatable(
        currentItemsPerPage,
        currentPage,
        criteria.value,
    );
}

async function onPaginate(e) {
    showSpinner();
    rows.value = e.rows;
    page.value = e.page + 1;
    setLocalStorageRowsPerPage(rows.value);
    populateDataTableWithLocalStorageFilters(rows.value, page.value);
}

function getLocalStorageData() {
    let storageData = localStorage.getItem(localStorageKey);
    
    if (null !== storageData && "undefined" !== storageData) {
        return JSON.parse(storageData);
    }

    return null;
}

function setLocalStorageRowsPerPage(rowPerPage: number) {
    let storageData = localStorage.getItem(localStorageKey);
    if (null !== storageData) {
        let localStorageObject = JSON.parse(storageData);
        localStorageObject.rows = rowPerPage;
        localStorage.setItem(localStorageKey, JSON.stringify(localStorageObject));
    }
}

function getLocalStorageRowsPerPage() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.rows) {
        return localStorageData.rows;
    }

    return defaultRowsPerPage;
}

function getLocalStorageFisrtPageState() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.first) {
        return localStorageData.first;
    }

    return 0;
}
</script>

<style scoped lang="scss">
@import "primevue/resources/themes/tailwind-light/theme.css";
@import "primevue/resources/primevue.min.css";
@import "primeicons/primeicons.css";

select {
    font-weight: 400;
}
.card-body {
    background-color: #fff;
}
::v-deep(table tr td:nth-child(2)) {
    cursor: pointer;
}
</style>